__webpack_public_path__ =
  process.env.ENV === 'development'
    ? process.env.WEBPACK_PUBLIC_PATH
    : `${window.$mediavine.web.model.wrapperSrc}/${window.$mediavine.web.model.versionGroup.version}/`

import { AuctionHouse } from './auctionHouse/AuctionHouse'
import Blocklist from './blocklist/Blocklist'
import Chicory from './chicory/Chicory'
import ComScore from './comScore/ComScore'
import GoogletagFacade from './googletagFacade/GoogletagFacade'
import GumGum from './partners/gumgum/GumGum'
import { WindowSize } from './helpers'
import { domReady, writeCss, addClass } from './helpers/DOM'
import Logger from './helpers/Logger'
import { LiftTest } from './liftTest/LiftTest'
import MetricsTracker from './metrics/MetricsTracker'
import WebModel, {
  optimizingForDesktopPagespeed,
  optimizingForMobilePagespeed
} from './models/WebModel'
import PageSettings from './pageSettings/PageSettings'
import PrebidFacade from './prebidFacade/PrebidFacade'
import Refresh from './refresh/Refresh'
import Adhesions from './renderers/Adhesions'
import ATF from './renderers/Atf'
import { ContentHints } from './renderers/ContentHints'
import DynamicContentSlots from './renderers/DynamicContentSlots'
import { GenericHints } from './renderers/GenericHints'
import Lazy from './renderers/Lazy'
import { ListHints } from './renderers/ListHints'
import InjectableCreative from './injectableCreative/InjectableCreative'
import SlotFactory from './slots/SlotFactory'
import { StickySlot } from './stickySlot/StickySlot'
import StickyVideo from './stickyVideo/StickyVideo'
import { getBaseStyles } from './styles/base'
import Zergnet from './zergnet/Zergnet'
import { IndexExchange } from './partners/indexExchange/IndexExchange'
import { AdReporter } from './adReporter/AdReporter'
import AdTags from './prebidFacade/videoBids/getAdTags'
import { doOnce } from './helpers/doOnce'
import Keywords from './keywords/Keywords'
import { Usersync } from './usersync/Usersync'
import { polyfill } from './helpers/polyfills'
import PageIdLookup from './pageIdLookup/PageIdLookup'
import { PnG } from './partners/PandG/PandG'
import { Scroll } from './helpers/Scroll'
import JumpToRecipe from './jumpToRecipe/JumpToRecipe'
import Dynamic from './dynamic/Dynamic'
// import { Session } from './session/Session'
// import { UTM } from './helpers/UTM'
import GrowFaves from './growFaves/GrowFaves'
import { Flooring } from './flooring/Flooring'
import { ArrivalSlot } from './renderers/ArrivalSlot'
import DynamicRecipeSlots from './renderers/DynamicRecipeSlots'
import { USP } from './cmp'

export const GDPRStub: IGDPR = {
  enableGdpr: false,
  onReady: (cb) => cb(),
  initModal: () => {}
}

if (window.$mediavine.web.model.gdpr === '1') {
  import(/* webpackChunkName: "gdpr"*/ './cmp/tcf/TcfStub')
    .then(({ makeStub }) => makeStub())
    .catch((error) => {
      const errorMessage = 'GDPR: Error loading "gdpr" webpackChunk'
      Logger.custom(['debug', 'gpdr'], errorMessage)
      window.$mediavine.newrelic.addPageAction('gdprError', {
        error,
        errorMessage
      })
    })
}

const debug = (msg: string) => Logger.custom(['debug', 'wrapper'], msg)

let model: WebModel
let ccpa
let gdpr: IGDPR = GDPRStub

const reRenderables: Array<{ render: Function }> = []

polyfill()

domReady(async () => {
  StickyVideo.setVideoTargetDivs(window.$mediavine.web.model)
  window.$mediavine.web.videoOptionsSet = true
})

export enum ExecutionStatus {
  halted,
  delayed,
  executed
}

export const init = async (): Promise<ExecutionStatus> => {
  if (window.$mediavine.web.initialized) {
    debug('Wrapper previously initialized. Halting Execution.')

    return ExecutionStatus.halted
  }

  addClass(document.body, 'mv-loaded')
  window.$mediavine.web.initialized = true
  window.$mediavine.web.load = doOnce(() =>
    load(model, blocklist, slotFactory, auctionHouse)
  )

  new AdReporter()

  await Flooring.requestGeolocation(window.$mediavine.web.model)

  // Flooring.requestGeolocation MUST be called before new WebModel.
  const geoLoc = await Flooring.requestGeolocation(window.$mediavine.web.model)
  model = new WebModel(window.$mediavine.web.model)
  model.geoLoc = geoLoc

  if (model.gdpr === '1') {
    try {
      const [{ TCF }, { GDPR }] = await Promise.all([
        import(/* webpackChunkName: "gdpr"*/ './cmp/tcf/TCF'),
        import(/* webpackChunkName: "gdpr"*/ './cmp/gdpr/GDPR')
      ])
      await TCF.init(model)
      gdpr = new GDPR(model)
      await gdpr.initModal()
    } catch (error) {
      const errorMessage = 'GDPR: Error loading "gdpr" webpackChunk'
      Logger.custom(['debug', 'gpdr'], errorMessage)
      window.$mediavine.newrelic.addPageAction('gdprError', {
        error,
        errorMessage
      })
    }
  } else {
    window.dispatchEvent(new CustomEvent('mediavineCmp', { detail: 'gdprDoesNotApply' }))
  }

  USP.init(model)

  if (model.ccpa === '1') {
    try {
      const [{ CCPA }] = await Promise.all([
        import(/* webpackChunkName: "ccpa"*/ './cmp/ccpa/CCPA')
      ])
      ccpa = new CCPA(model)
      await ccpa.initModal()
    } catch (error) {
      const errorMessage = 'CCPA: Error loading "ccpa" webpackChunk'
      Logger.custom(['debug', 'ccpa'], errorMessage)
      window.$mediavine.newrelic.addPageAction('ccpaError', {
        error,
        errorMessage
      })
    }
  }

  writeCss(getBaseStyles(model))
  const pageSettings = new PageSettings()
  const blocklist = new Blocklist(model, pageSettings)
  const auctionHouse = new AuctionHouse(model)
  const slotFactory = new SlotFactory(model, blocklist, auctionHouse)
  reRenderables.push(new DynamicContentSlots(model, slotFactory))
  reRenderables.push(new StickyVideo(model, blocklist))
  ArrivalSlot.attachListener(model, window.$mediavine.web.load)

  new JumpToRecipe(model)

  if (optimizingForMobilePagespeed() || optimizingForDesktopPagespeed()) {
    debug('Pagespeed Optimization. Delaying execution until scroll.')

    const callback = () => {
      debug('Scroll detected or waited too long. Executing.')
      window.removeEventListener('scroll', callback, false)
      window.removeEventListener('beforeprint', callback, false)

      window.$mediavine.web.load()
    }

    window.addEventListener('scroll', callback, false)
    window.addEventListener('beforeprint', callback, false)

    // Alternatively, force load after 3 seconds of waiting for scroll
    // const loadAfterMs = 3100 //ms
    // setTimeout(callback, loadAfterMs)

    return ExecutionStatus.delayed
  } else {
    window.$mediavine.web.load()

    return ExecutionStatus.executed
  }
}

export const load = async (
  model: WebModel,
  blocklist: Blocklist,
  slotFactory: SlotFactory,
  auctionHouse: AuctionHouse
): Promise<ExecutionStatus> => {
  /** Render Validations */
  if (
    domainIsNotValid() ||
    siteIsDeactivated(model) ||
    siteIsKillswitched(model) ||
    onPrivacyPolicyPage(model) ||
    windowIsTooSmall()
  ) {
    return ExecutionStatus.halted
  }

  const keywordService = new Keywords()
  new PageIdLookup(model)
  await PrebidFacade.loadPrebidImports()

  const liftTest = LiftTest.new(model)
  const adTags = new AdTags(model, keywordService, liftTest, auctionHouse)

  window.$mediavine.web.getVideoAdTag = adTags.getAdTag.bind(adTags)
  window.$mediavine.web.getVideoAdTagAndHighBid = adTags.getAdTagAndHighBid.bind(
    adTags
  )
  window.$mediavine.web.getNextVideoAdTagAndHighBid = adTags.getNextAdTagAndHighBid.bind(
    adTags
  )
  window.$mediavine.web.getNonLinearVideoAdTag = adTags.getNonLinearAdTag.bind(
    adTags
  )
  window.$mediavine.web.auctionHouse = auctionHouse

  new GrowFaves(model)

  if (blocklist.all) {
    debug('Blocklist.all triggered. Halting execution.')
    // Footer branding is omitted from global blocklisting
    new ComScore(model)
    return ExecutionStatus.halted
  }

  gdpr.onReady(() => {
    /**
     * Wait until the modal ineraction is done before calling
     * GoogleTagFacade, as with TCF2 Google obtains the tcData on their own
     */
    new GoogletagFacade(model, auctionHouse, keywordService)

    // Disable personalized ads and usersync if the user is in Brazil
    if (model.geoLoc.country_code === 'BR') {
      GoogletagFacade.onReady(() => {
        window.googletag.pubads().setRequestNonPersonalizedAds(1)
      }).catch()
    } else {
      Usersync.init(model)
    }
    IndexExchange.init(model, blocklist)

    const prebidFacade = new PrebidFacade(model, gdpr, auctionHouse)
    Scroll.init()

    // new Session(model.gdpr)
    // new UTM(model.gdpr)

    // Feature Modules
    new MetricsTracker(model)
    new InjectableCreative(model)
    new StickySlot(model)
    const refresh = new Refresh(prebidFacade)
    new Chicory(model, blocklist)

    // Make all content slots lazyables
    Dynamic.attachPrebidFacade(prebidFacade)

    // Ad Renderers
    new Adhesions(model, slotFactory, prebidFacade, blocklist)
    new ATF(model, slotFactory, prebidFacade)
    reRenderables.push(new Lazy(model, slotFactory, prebidFacade))
    reRenderables.push(new ListHints(prebidFacade, slotFactory))
    reRenderables.push(new ContentHints(prebidFacade, slotFactory))
    reRenderables.push(new GenericHints(prebidFacade, slotFactory))
    reRenderables.push(new ArrivalSlot(model, slotFactory, prebidFacade))
    reRenderables.push(new DynamicRecipeSlots(model, slotFactory))

    setInterval(() => reRenderables.forEach((r) => r.render()), 1000)

    // third-party partner modules
    new ComScore(model)
    new GumGum(model, blocklist, refresh)
    new Zergnet(model, blocklist)
    PnG.writePixel(model, gdpr)
  })

  return ExecutionStatus.executed
}

window.$mediavine.web.init = init

function domainIsNotValid(): boolean {
  const isNotValid =
    window.$mediavine.web.model.whitelistDomain &&
    !!window.$mediavine.web.model.domain &&
    window.$mediavine.web.model.domain !== window.location.hostname

  if (isNotValid) {
    debug('Domain not whitelisted. Halting Execution.')
  }

  return isNotValid
}

const validate = (bool: boolean, errMessage: string): boolean => {
  if (bool) {
    debug(`${errMessage}. Halting execution.`)
  }
  return bool
}

const siteIsDeactivated = (model): boolean =>
  validate(model.deactivated, 'Site has been deactivated.')
const siteIsKillswitched = (model): boolean =>
  validate(model.killswitch, 'Site killswitch is activated.')
const windowIsTooSmall = (): boolean =>
  validate(WindowSize.isWindowTooSmall(), 'Window is too small.')
export const onPrivacyPolicyPage = (model): boolean =>
  validate(
    model.privacy_policy_link ===
      `${window.location.origin}${window.location.pathname}`,
    "On site's privacy page."
  )
